import { FocusTrap, FocusTrapFeatures } from '@headlessui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import * as React from 'react';
import DialogShell from 'components/DialogShell';
import { getDealerSelectionErrorMessage } from 'utils/auth/dealerResolution';
import type { IDealerSelectionDialogProps } from 'utils/auth/dealerResolution.types';

const FOCUS_TRAP_FEATURES =
  FocusTrapFeatures.InitialFocus |
  FocusTrapFeatures.TabLock |
  FocusTrapFeatures.FocusLock |
  FocusTrapFeatures.RestoreFocus;

/**
 * Blocking dealer selection step (AVS-4405). Rendered only by `SecureOutlet` while the dealer
 * gate is pending a choice: no close control, no Escape handling, no backdrop click-through —
 * the only way out is picking one of the options.
 */
export default function DealerSelectionDialog({
  options,
  brand,
  market,
  error,
  onSelect,
}: IDealerSelectionDialogProps) {
  const { i18n } = useLingui();

  return (
    <FocusTrap features={FOCUS_TRAP_FEATURES}>
      <DialogShell
        panelProps={{
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'dealer-selection-dialog-title',
        }}
        panelTestId="dealer-selection-dialog"
      >
        <h2
          part="dialog-title"
          id="dealer-selection-dialog-title"
          className="text-base font-semibold"
          data-testid="dealer-selection-dialog-title"
        >
          <Trans>Choose your Dealer ID</Trans>
        </h2>
        <p className="mt-2 text-sm">
          <Trans>To start the experience, select the Dealer ID you want to work with.</Trans>
        </p>
        {error && (
          <p
            role="alert"
            part="dialog-message"
            data-testid="dealer-selection-error"
            className="mt-2 text-sm text-red-700"
          >
            {i18n._(getDealerSelectionErrorMessage(error, brand, market))}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2">
          {options.map((dealerId) => (
            <button
              key={dealerId}
              type="button"
              part="dialog-option"
              data-testid={`dealer-selection-option-${dealerId}`}
              className="w-full rounded-md p-3 text-left text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
              style={{
                backgroundColor: 'var(--ex3d-dialog-confirm-bg-color, var(--ex3d-dealer-primary))',
                color: 'var(--ex3d-dialog-confirm-text-color, var(--ex3d-dealer-white))',
              }}
              onClick={() => onSelect(dealerId)}
            >
              {dealerId}
            </button>
          ))}
        </div>
      </DialogShell>
    </FocusTrap>
  );
}
